import React, { useState, useRef } from 'react';
import html2canvas from 'html2canvas';
import './ItemCard.css';
import api from '../services/api';
import { getOrCreateUserId } from '../utils/userId';

function ItemCard({ item, onEdit, isAdmin = false, onReactionUpdate, onDelete }) {
  const cardRef = useRef(null);
  const [userReaction, setUserReaction] = useState(item.userReaction || null);
  const [reactionCounts, setReactionCounts] = useState({
    likes: item.likes || 0,
    dislikes: item.dislikes || 0,
    heatUps: item.heatUps || 0
  });
  const [reacting, setReacting] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [shareFeedback, setShareFeedback] = useState(null);
  const formatPrice = (price) => {
    if (!price) return 'N/A';
    return new Intl.NumberFormat('en-US').format(price);
  };

  const getRarityColor = (rarity) => {
    const colors = {
      yellow: '#ffd700',
      orange: '#e9a030',
      red: '#ff4444',
      blue: '#4444ff',
      green: '#44ff44',
      purple: '#aa44ff',
    };
    return colors[rarity?.toLowerCase()] || '#888';
  };

  const getRarityBadgeStyle = (rarity) => {
    const r = rarity?.toLowerCase();
    const styles = {
      yellow: { background: 'rgba(254, 255, 196, 0.9)', color: '#b8860b' },
      orange: { background: 'rgba(216, 143, 46, 0.5)', color: '#c2410c' },
      red: { background: 'rgba(234, 207, 209, 0.9)', color: '#b91c1c' },
      blue: { background: 'rgba(180, 200, 255, 0.9)', color: '#1e40af' },
      green: { background: 'rgba(200, 255, 200, 0.9)', color: '#15803d' },
      purple: { background: 'rgba(220, 180, 255, 0.9)', color: '#6b21a8' },
    };
    return styles[r] || { background: 'rgba(136, 136, 136, 0.3)', color: '#888' };
  };

  const getImageContainerBackground = (rarity) => {
    const r = rarity?.toLowerCase();
    // rgba with ~0.5 alpha for subtle transparency
    if (r === 'yellow') return 'rgba(254, 255, 196, 0.5)';
    if (r === 'red') return 'rgba(234, 207, 209, 0.5)';
    if (r === 'orange') return 'rgba(216, 143, 46, 0.5)';
    if (r === 'blue' || r === 'green' || r === 'purple') return 'rgba(233, 218, 198, 0.5)';
    return null;
  };

  const imageContainerBg = getImageContainerBackground(item.rarity);

  async function handleReaction(reaction) {
    if (reacting) return;
    
    setReacting(true);
    try {
      const userId = getOrCreateUserId();
      const response = await api.reactToItem(item.id, reaction, userId);
      
      setUserReaction(response.userReaction);
      setReactionCounts({
        likes: response.item.likes,
        dislikes: response.item.dislikes,
        heatUps: response.item.heatUps
      });
      
      if (onReactionUpdate) {
        onReactionUpdate(response.item);
      }
    } catch (error) {
      console.error('Reaction error:', error);
      alert(error.message || 'Failed to react to item');
    } finally {
      setReacting(false);
    }
  }

  async function handleShare() {
    if (sharing || !cardRef.current) return;
    setSharing(true);
    setShareFeedback(null);
    try {
      const proxyBase = `${window.location.origin}/.netlify/functions/proxy-image`;
      const isTornImage = (url) => url && /torn\.com\/images\//i.test(url);

      // Temporarily swap Torn img src to proxied URLs so html2canvas can load them (no CORS)
      const imgs = cardRef.current.querySelectorAll('img[src]');
      const originals = [];
      imgs.forEach((img) => {
        const src = img.getAttribute('src');
        if (isTornImage(src)) {
          originals.push({ img, src });
          img.src = `${proxyBase}?url=${encodeURIComponent(src)}`;
        }
      });

      // Wait for swapped images to load before capturing
      await Promise.all(
        originals.map(({ img }) =>
          img.complete
            ? Promise.resolve()
            : new Promise((resolve) => {
                img.onload = resolve;
                img.onerror = resolve;
                setTimeout(resolve, 3000);
              })
        )
      );

      const canvas = await html2canvas(cardRef.current, {
        useCORS: true,
        allowTaint: false,
        backgroundColor: '#2a2a2a',
        scale: 2,
        ignoreElements: (el) => el.closest('.share-hide') !== null,
      });
      const dataUrl = canvas.toDataURL('image/png');

      // Restore original img src
      originals.forEach(({ img, src }) => {
        img.src = src;
      });

      const { url } = await api.saveCardImage(item.id, dataUrl);
      await navigator.clipboard.writeText(url);
      setShareFeedback('Link copied!');
      setTimeout(() => setShareFeedback(null), 3000);
    } catch (error) {
      console.error('Share error:', error);
      setShareFeedback(error.message || 'Failed to share');
      setTimeout(() => setShareFeedback(null), 5000);
    } finally {
      setSharing(false);
    }
  }

  const isSold = item.isSold || false;

  return (
    <div ref={cardRef} className={`item-card ${isSold ? 'item-sold' : ''}`}>
      <div className="item-header">
        <h3 className="item-name">{item.name}</h3>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          {isSold && (
            <span className="sold-badge" style={{ 
              background: '#ff4444', 
              color: '#fff', 
              padding: '0.25rem 0.5rem', 
              borderRadius: '4px', 
              fontSize: '0.75rem',
              fontWeight: '600',
              textTransform: 'uppercase'
            }}>
              SOLD
            </span>
          )}
          {item.rarity && (
            <span
              className="item-rarity item-rarity-badge"
              style={{
                ...getRarityBadgeStyle(item.rarity),
                padding: '0.25rem 0.5rem',
                borderRadius: '4px',
                fontSize: '0.75rem',
                fontWeight: '600',
              }}
            >
              {item.rarity}
            </span>
          )}
        </div>
      </div>

      {item.image && (
        <div
          className="item-image-container"
          style={imageContainerBg ? { background: imageContainerBg, borderColor: 'rgba(255,255,255,0.15)' } : undefined}
        >
          <img 
            src={item.image} 
            alt={item.name}
            className="item-image"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        </div>
      )}

      <div className="item-info">
        <div className="info-row">
          <span className="info-label">Type:</span>
          <span className="info-value">{item.type}</span>
        </div>
        {item.subType && (
          <div className="info-row">
            <span className="info-label">Sub Type:</span>
            <span className="info-value">{item.subType}</span>
          </div>
        )}
        {item.myPrice !== null && item.myPrice !== undefined && (
          <div className="info-row highlight">
            <span className="info-label">Buyout price:</span>
            <span className="info-value">${formatPrice(item.myPrice)}</span>
          </div>
        )}
        {item.sellerId && (
          <div className="info-row">
            <span className="info-label">Sold by:</span>
            <a
              href={`https://www.torn.com/profiles.php?XID=${item.sellerId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="info-value seller-link"
            >
              {item.sellerName || 'View profile'}
            </a>
          </div>
        )}
      </div>

      {(item.damage !== null && item.damage !== undefined) ||
       (item.accuracy !== null && item.accuracy !== undefined) ||
       (item.armor !== null && item.armor !== undefined) ||
       (item.quality !== null && item.quality !== undefined) ? (
        <div className="item-stats">
          <h4 className="stats-title">Stats</h4>
          <div className="stats-grid">
            {item.damage !== null && item.damage !== undefined && (
              <div className="stat">
                <span className="stat-label">Damage:</span>
                <span className="stat-value">{item.damage.toFixed(2)}</span>
              </div>
            )}
            {item.accuracy !== null && item.accuracy !== undefined && (
              <div className="stat">
                <span className="stat-label">Accuracy:</span>
                <span className="stat-value">{item.accuracy.toFixed(2)}</span>
              </div>
            )}
            {item.armor !== null && item.armor !== undefined && (
              <div className="stat">
                <span className="stat-label">Armor:</span>
                <span className="stat-value">{item.armor.toFixed(2)}</span>
              </div>
            )}
            {item.quality !== null && item.quality !== undefined && (
              <div className="stat">
                <span className="stat-label">Quality:</span>
                <span className="stat-value">{item.quality.toFixed(2)}%</span>
              </div>
            )}
          </div>
        </div>
      ) : null}

      {(() => {
        let bonuses = [];
        if (item.bonuses) {
          try {
            bonuses = Array.isArray(item.bonuses) 
              ? item.bonuses 
              : (typeof item.bonuses === 'string' ? JSON.parse(item.bonuses) : item.bonuses);
            if (!Array.isArray(bonuses)) bonuses = [];
          } catch (e) {
            bonuses = [];
          }
        }
        
        return bonuses.length > 0 ? (
          <div className="item-bonuses">
            <h4 className="bonuses-title">Bonuses</h4>
            <div className="bonuses-list">
              {bonuses.map((bonus, index) => (
                <div key={index} className="bonus-item">
                  <div className="bonus-header">
                    <span className="bonus-title">{bonus.title}</span>
                    {bonus.value !== null && bonus.value !== undefined && (
                      <span className="bonus-value">{bonus.value}%</span>
                    )}
                  </div>
                  {bonus.description && (
                    <p className="bonus-description">{bonus.description}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        ) : null;
      })()}

      {item.myDescription && (
        <div className="item-description">
          <p>{item.myDescription}</p>
        </div>
      )}

      <div className="item-reactions share-hide">
        <button
          className={`reaction-button heat-button ${userReaction?.heatedUp ? 'active' : ''}`}
          onClick={() => handleReaction('heat')}
          disabled={reacting || isSold}
          title={isSold ? 'This item is sold' : `This item is heated up! ${reactionCounts.heatUps} user${reactionCounts.heatUps !== 1 ? 's' : ''} heat!`}
        >
          🔥 {reactionCounts.heatUps}
        </button>
        <button
          className={`reaction-button like-button ${userReaction?.liked ? 'active' : ''}`}
          onClick={() => handleReaction('like')}
          disabled={reacting || userReaction?.disliked || isSold}
          title={isSold ? 'This item is sold' : `${reactionCounts.likes} user${reactionCounts.likes !== 1 ? 's' : ''} liked this item`}
        >
          👍 {reactionCounts.likes}
        </button>
        <button
          className={`reaction-button dislike-button ${userReaction?.disliked ? 'active' : ''}`}
          onClick={() => handleReaction('dislike')}
          disabled={reacting || userReaction?.liked || isSold}
          title={isSold ? 'This item is sold' : `${reactionCounts.dislikes} user${reactionCounts.dislikes !== 1 ? 's' : ''} disliked this item`}
        >
          👎 {reactionCounts.dislikes}
        </button>
        {isAdmin && (
          <button
            className="reaction-button share-button"
            onClick={handleShare}
            disabled={sharing}
            title="Share card image link (for Torn forums etc.)"
          >
            {sharing ? '…' : shareFeedback || '🔗 Share'}
          </button>
        )}
      </div>

      {isAdmin && (
        <div className="admin-actions share-hide" style={{ display: 'flex', gap: '0.5rem', marginTop: 'auto' }}>
          {onEdit && (
            <button className="edit-button" onClick={() => onEdit(item)}>
              Edit
            </button>
          )}
          {onDelete && (
            <button 
              className="delete-button" 
              onClick={() => {
                if (window.confirm(`Are you sure you want to delete "${item.name}"? This action cannot be undone.`)) {
                  onDelete(item.id);
                }
              }}
            >
              Delete
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default ItemCard;
